import { useCallback, useMemo } from 'react';
import type { Layout, LayoutItem } from 'react-grid-layout';

import { chartMinPx, etcSectionMinPx, gridCols, orderbookMinPx } from './constants';
import { gridUnitsToPx, pxToGridUnits } from './layout-utils';

interface Options {
  visibleLayouts: LayoutItem[];
  gridWidth: number;
  gridMounted: boolean;
  updateLayouts: (layouts: LayoutItem[]) => void;
  isShowOrderbook: boolean;
}

/**
 * Handles two kinds of render-only layout overrides for the orderbook panel.
 * Neither override is ever written to storage.
 *
 * 1. System hiding (isShowOrderbook = false):
 *    Removes orderbook from the grid and expands chart to fill the gap.
 *    This happens when the symbol doesn't support the orderbook (e.g. spot without limit-order flag).
 *
 * 2. Compact mode:
 *    Activates when chart or orderbook would become narrower than their minimum pixel thresholds.
 *    Both panels expand to full width and stack vertically.
 *    Panel widths are stored as grid column units and scale proportionally with browser width.
 */
export function useGridLayout({ visibleLayouts, gridWidth, gridMounted, updateLayouts, isShowOrderbook }: Options) {
  // Step 1: system-level hiding — render-only, never persisted.
  const systemLayouts = useMemo(() => {
    if (isShowOrderbook) return visibleLayouts;

    const orderbookItem = visibleLayouts.find((l) => l.i === 'orderbook');
    // User already manually hidden orderbook — nothing to override.
    if (!orderbookItem) return visibleLayouts;

    return visibleLayouts
      .filter((l) => l.i !== 'orderbook')
      .map((l) => {
        if (l.i === 'chart' && l.y === orderbookItem.y) {
          return { ...l, w: Math.min(l.w + orderbookItem.w, gridCols) };
        }
        return l;
      });
  }, [visibleLayouts, isShowOrderbook]);

  // Step 2: visual-only overrides on top of system layouts (never persisted).
  // Overrides are applied in sequence so multiple panels can be adjusted in one pass.
  const renderLayouts = useMemo(() => {
    if (!gridMounted) return systemLayouts;

    let result = systemLayouts;

    // Orderbook: compact mode or independent minimum width.
    const ob = result.find((l) => l.i === 'orderbook');
    if (ob) {
      const chart = result.find((l) => l.i === 'chart');
      const shareRow = chart != null && chart.y === ob.y && chart.x + chart.w === ob.x;

      if (shareRow && chart) {
        // Compact mode: when chart or orderbook is too narrow, stack them full-width vertically.
        const chartPx = gridUnitsToPx(chart.w, gridWidth);
        const obPx = gridUnitsToPx(ob.w, gridWidth);

        if (chartPx < chartMinPx || obPx < orderbookMinPx) {
          const chartBottom = chart.y + chart.h;
          result = result.map((l) => {
            if (l.i === 'chart') return { ...l, w: gridCols };
            if (l.i === 'orderbook') return { ...l, x: 0, y: chartBottom, w: gridCols };
            return l;
          });
        }
      } else {
        // Independent orderbook: enforce minimum pixel width visually.
        const obPx = gridUnitsToPx(ob.w, gridWidth);
        if (obPx < orderbookMinPx) {
          const minW = pxToGridUnits(orderbookMinPx, gridWidth);
          result = result.map((l) => (l.i === 'orderbook' ? { ...l, w: Math.max(l.w, minW) } : l));
        }
      }
    }

    // etcSection: enforce minimum pixel width visually.
    const etc = result.find((l) => l.i === 'etcSection');
    if (etc) {
      const etcPx = gridUnitsToPx(etc.w, gridWidth);
      if (etcPx < etcSectionMinPx) {
        const minW = pxToGridUnits(etcSectionMinPx, gridWidth);
        result = result.map((l) => (l.i === 'etcSection' ? { ...l, w: Math.max(l.w, minW) } : l));
      }
    }

    return result;
  }, [gridWidth, gridMounted, systemLayouts]);

  // Compact mode: both chart and orderbook span full width (orderbook below chart).
  const isCompact = useMemo(() => {
    if (!gridMounted) return false;
    const ob = renderLayouts.find((l) => l.i === 'orderbook');
    const chart = renderLayouts.find((l) => l.i === 'chart');
    if (!ob || !chart) return false;
    return ob.w === gridCols && chart.w === gridCols;
  }, [gridMounted, renderLayouts]);

  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      // Skip persisting compact mode layout — it's a visual-only adaptation to narrow viewports.
      if (isCompact) return;

      const incoming = newLayout as LayoutItem[];

      if (!isShowOrderbook) {
        // System-hidden orderbook: restore chart to stored width and re-add orderbook layout so
        // it isn't dropped from storage. (updateLayouts only merges panels in hiddenPanels,
        // but orderbook is not user-hidden.)
        const storedChart = visibleLayouts.find((l) => l.i === 'chart');
        const storedOrderbook = visibleLayouts.find((l) => l.i === 'orderbook');

        let restored = incoming;
        if (storedChart) {
          restored = restored.map((l) => (l.i === 'chart' ? { ...l, w: storedChart.w } : l));
        }
        if (storedOrderbook && !restored.some((l) => l.i === 'orderbook')) {
          restored = [...restored, storedOrderbook];
        }
        updateLayouts(restored);
        return;
      }

      // If a panel was visually expanded to meet its minimum pixel width and the user didn't
      // resize it wider, restore the stored w before persisting so the expansion is never saved.
      const restoreStoredW = (panelId: string, layouts: LayoutItem[]): LayoutItem[] => {
        const stored = visibleLayouts.find((l) => l.i === panelId);
        const rendered = renderLayouts.find((l) => l.i === panelId);
        const incoming_ = layouts.find((l) => l.i === panelId);
        if (stored && rendered && incoming_ && stored.w !== rendered.w && incoming_.w === rendered.w) {
          return layouts.map((l) => (l.i === panelId ? { ...l, w: stored.w } : l));
        }
        return layouts;
      };

      updateLayouts(restoreStoredW('etcSection', restoreStoredW('orderbook', incoming)));
    },
    [updateLayouts, isCompact, isShowOrderbook, visibleLayouts, renderLayouts],
  );

  return { renderLayouts, isCompact, handleLayoutChange };
}
